import React, { useCallback, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { HiSparkles } from 'react-icons/hi2'
import ChatMessage from './ChatMessage'
import ChatInput from './ChatInput'
import useThemePalette from 'utils/hooks/useThemePalette'
import { getVisibleChatMessages } from 'store/chat/chatUtils'

const NEAR_BOTTOM_THRESHOLD_PX = 96

const getMessageKey = (msg) => {
    if (msg?.id) return String(msg.id)
    if (msg?.rowKey) return String(msg.rowKey)

    const role = msg?.isUser ? 'user' : 'assistant'
    const timestamp = msg?.timestamp || 'no-time'
    const text = typeof msg?.text === 'string' ? msg.text.slice(0, 48) : ''
    return `${role}-${timestamp}-${text}`
}

const TypingIndicator = ({ isDark }) => (
    <div className="flex items-end gap-2 mb-4">
        {/* Bot avatar */}
        <div
            className={classNames(
                'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white mb-1',
                isDark ? 'bg-gray-600' : 'bg-gray-400'
            )}
        >
            <HiSparkles className="w-4 h-4" />
        </div>
        {/* Bubble */}
        <div
            className={classNames(
                'px-4 py-3.5 rounded-2xl rounded-bl-sm shadow-sm',
                isDark
                    ? 'bg-gray-700 border border-gray-600'
                    : 'bg-gray-100 border border-gray-200'
            )}
        >
            <div className="flex items-center gap-1.5">
                <span
                    className={classNames(
                        'w-2 h-2 rounded-full animate-bounce',
                        isDark ? 'bg-gray-400' : 'bg-gray-400'
                    )}
                    style={{ animationDelay: '0ms', animationDuration: '900ms' }}
                />
                <span
                    className={classNames(
                        'w-2 h-2 rounded-full animate-bounce',
                        isDark ? 'bg-gray-400' : 'bg-gray-400'
                    )}
                    style={{ animationDelay: '180ms', animationDuration: '900ms' }}
                />
                <span
                    className={classNames(
                        'w-2 h-2 rounded-full animate-bounce',
                        isDark ? 'bg-gray-400' : 'bg-gray-400'
                    )}
                    style={{ animationDelay: '360ms', animationDuration: '900ms' }}
                />
            </div>
        </div>
    </div>
)

const ChatPanel = ({
    messages = [],
    onSendMessage,
    loading = false,
    height = '300px',
    className = '',
    contextLabel = '',
    variant = 'default',
    showContextLabel = true,
    emptyStateTitle = '',
    emptyStateDescription = '',
    inputProps = {},
    hideWelcomeMessages = false,
}) => {
    const palette = useThemePalette()
    const { isDark } = palette
    const scrollAreaRef = useRef(null)
    const previousMessageCountRef = useRef(0)
    const wasNearBottomRef = useRef(true)
    const renderedMessages = hideWelcomeMessages
        ? getVisibleChatMessages(messages)
        : Array.isArray(messages)
            ? messages
            : []
    const isWorkspaceVariant = variant === 'workspace'

    const updateNearBottom = useCallback(() => {
        const element = scrollAreaRef.current
        if (!element) {
            wasNearBottomRef.current = true
            return
        }

        const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight
        wasNearBottomRef.current = distanceFromBottom < NEAR_BOTTOM_THRESHOLD_PX
    }, [])

    const scrollToBottom = useCallback((behavior = 'auto') => {
        const element = scrollAreaRef.current
        if (!element) {
            return
        }

        if (typeof element.scrollTo === 'function') {
            element.scrollTo({ top: element.scrollHeight, behavior })
        } else {
            element.scrollTop = element.scrollHeight
        }

        updateNearBottom()
    }, [updateNearBottom])

    useEffect(() => {
        const messageCount = renderedMessages.length
        const lastMessage = renderedMessages[messageCount - 1]
        const previousMessageCount = previousMessageCountRef.current
        const isInitialRender = previousMessageCount === 0
        const messageAdded = messageCount > previousMessageCount
        const shouldScroll =
            isInitialRender ||
            wasNearBottomRef.current ||
            Boolean(lastMessage?.isUser) ||
            (loading && messageAdded)

        previousMessageCountRef.current = messageCount

        if (shouldScroll) {
            window.requestAnimationFrame(() => {
                scrollToBottom(isInitialRender ? 'auto' : 'smooth')
            })
        }
    }, [renderedMessages, loading, scrollToBottom])

    const containerClass = classNames(
        isWorkspaceVariant
            ? 'flex h-full flex-col overflow-hidden'
            : 'flex flex-col overflow-hidden border rounded-2xl',
        className
    )

    const scrollAreaClass = classNames(
        isWorkspaceVariant
            ? 'flex-1 overflow-y-auto min-h-0 px-6 py-6'
            : 'flex-1 overflow-y-auto min-h-0 px-4 pt-4 pb-2'
    )

    const borderClass = isDark
        ? 'border-t border-gray-700'
        : 'border-t border-gray-200'
    const bannerClass = isDark
        ? 'border-b border-gray-700 bg-gray-800/80 text-gray-200'
        : 'border-b border-gray-200 bg-blue-50 text-blue-900'
    const resolvedEmptyTitle =
        emptyStateTitle ||
        (isWorkspaceVariant ? 'How can I help today?' : 'Ready when you are')
    const resolvedEmptyDescription =
        emptyStateDescription ||
        (isWorkspaceVariant
            ? 'Ask about a ticket, client context, KB article, or troubleshooting path.'
            : 'Ask me anything about this ticket…')

    return (
        <div className={containerClass} style={{ height }}>
            {showContextLabel && contextLabel && (
                <div className={classNames('px-4 py-2 text-xs font-semibold', bannerClass)}>
                    Context: {contextLabel}
                </div>
            )}
            {/* Messages scrollable area */}
            <div
                ref={scrollAreaRef}
                className={scrollAreaClass}
                onScroll={updateNearBottom}
            >
                {renderedMessages.length === 0 && !loading ? (
                    <div
                        className={classNames(
                            'flex h-full flex-col items-center justify-center gap-3 pb-8 text-center',
                            isDark ? 'text-gray-500' : 'text-gray-400'
                        )}
                    >
                        <HiSparkles
                            className={classNames(
                                isWorkspaceVariant ? 'h-10 w-10' : 'h-8 w-8',
                                'opacity-40'
                            )}
                        />
                        <div>
                            <p className="text-base font-semibold text-gray-700 dark:text-gray-200">
                                {resolvedEmptyTitle}
                            </p>
                            <p className="mt-1 max-w-xl text-sm leading-relaxed">
                                {resolvedEmptyDescription}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {renderedMessages.map((msg) =>
                            msg ? (
                                <ChatMessage
                                    key={getMessageKey(msg)}
                                    id={getMessageKey(msg)}
                                    message={msg.text}
                                    isUser={msg.isUser}
                                    timestamp={msg.timestamp}
                                    attachments={msg.attachments}
                                    artifacts={msg.artifacts}
                                />
                            ) : null
                        )}
                        {loading && <TypingIndicator isDark={isDark} />}
                    </>
                )}
            </div>

            {/* Input bar */}
            <div className={classNames('flex-shrink-0', borderClass)}>
                <ChatInput
                    onSendMessage={onSendMessage}
                    disabled={loading}
                    loading={loading}
                    isDark={isDark}
                    variant={isWorkspaceVariant ? 'workspace' : 'default'}
                    {...inputProps}
                />
            </div>
        </div>
    )
}

export default ChatPanel